import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Select, Space, Switch, Divider, Tag, Tooltip, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from '../../api';
import theme from '../../theme';

// 主流大模型选项
const modelOptions = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'deepseek', label: 'Deepseek' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'qwen', label: '通义千问' },
  { value: 'moonshot', label: 'Moonshot' },
];

// 常用Model Name推荐
const commonModelNames = {
  openai: [
    { value: 'gpt-4o', label: 'GPT-4o', description: '最新的GPT-4模型，支持多模态，性能优秀' },
    { value: 'gpt-4o-mini', label: 'GPT-4o-mini', description: 'GPT-4o的轻量版本，性价比高' },
    { value: 'gpt-4-turbo', label: 'GPT-4-turbo', description: 'GPT-4 Turbo模型，平衡性能和成本' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5-turbo', description: 'GPT-3.5 Turbo，快速响应' },
  ],
  deepseek: [
    { value: 'deepseek-chat', label: 'deepseek-chat', description: 'DeepSeek对话模型' },
    { value: 'deepseek-coder', label: 'deepseek-coder', description: 'DeepSeek代码生成模型' },
  ],
  gemini: [
    { value: 'gemini-1.5-pro', label: 'gemini-1.5-pro', description: 'Gemini 1.5 Pro，多模态支持' },
    { value: 'gemini-1.5-flash', label: 'gemini-1.5-flash', description: 'Gemini 1.5 Flash，快速响应' },
  ],
  qwen: [
    { value: 'qwen-turbo', label: 'qwen-turbo', description: '通义千问Turbo版本' },
    { value: 'qwen-plus', label: 'qwen-plus', description: '通义千问Plus版本' },
    { value: 'qwen-max', label: 'qwen-max', description: '通义千问Max版本' },
  ],
  moonshot: [
    { value: 'moonshot-v1-8k', label: 'moonshot-v1-8k', description: 'Moonshot V1 8K上下文' },
    { value: 'moonshot-v1-32k', label: 'moonshot-v1-32k', description: 'Moonshot V1 32K上下文' },
  ],
};

// 联网模式选项
const networkModeOptions = [
  { value: 'none', label: '不联网' },
  { value: 'browsing', label: '网页浏览' },
  { value: 'search', label: '搜索查询' },
  { value: 'full', label: '完全联网' },
];

// 搜索引擎选项
const searchEngineOptions = [
  { value: 'google', label: 'Google' },
  { value: 'bing', label: 'Bing' },
  { value: 'duckduckgo', label: 'DuckDuckGo' },
  { value: 'serper', label: 'Serper' },
];

const ApiManage = () => {
  // API列表数据
  const [apis, setApis] = useState([]);
  // 加载状态
  const [loading, setLoading] = useState(false);
  // 弹窗显示状态
  const [modalVisible, setModalVisible] = useState(false);
  // antd表单实例
  const [form] = Form.useForm();
  // 当前编辑的API对象
  const [editingApi, setEditingApi] = useState(null);
  // 搜索关键词
  const [searchText, setSearchText] = useState('');

  // 获取API列表
  const fetchApis = async () => {
    setLoading(true);
    try {
      console.log('[ApiManage] 请求大模型API列表...');
      const res = await axios.get('/api/modelapis/');
      console.log('[ApiManage] API列表响应:', res.data);
      setApis(res.data);
      // 将API列表保存到全局变量，供getModelApiConfig使用
      window.__apis = res.data;
    } catch (err) {
      console.error('[ApiManage] 获取API列表失败:', err);
      message.error('获取API列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 首次加载时获取API列表
  useEffect(() => {
    fetchApis();
  }, []);

  // 打开新增/编辑弹窗
  const handleAddEdit = (record = null) => {
    setEditingApi(record);
    if (record) {
      console.log('[ApiManage] 编辑API，回显数据:', record);
      form.setFieldsValue(record);
    } else {
      console.log('[ApiManage] 新增API，重置表单');
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 监听表单字段变化
  const handleFormValuesChange = (changedValues, allValues) => {
    // 当大模型类型改变时，自动推荐Model Name
    if (changedValues.model && !allValues.model_name) {
      const recommendations = commonModelNames[changedValues.model] || [];
      if (recommendations.length > 0) {
        form.setFieldsValue({
          model_name: recommendations[0].value,
          model_name_description: recommendations[0].description
        });
      }
    }
  };

  // 提交表单（新增/编辑）
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('[ApiManage] 表单校验通过，提交内容：', values);
      
      // 检查是否已存在相同的API Key + Model Name组合
      const existingApi = apis.find(api => 
        api.apikey === values.apikey && 
        api.model_name === values.model_name &&
        (!editingApi || api.id !== editingApi.id)
      );
      
      if (existingApi) {
        message.error('该API Key已配置了相同的Model Name，请选择其他Model Name或使用不同的API Key');
        return;
      }
      
      if (editingApi) {
        console.log('[ApiManage] 更新API:', editingApi.id, values);
        await axios.put(`/api/modelapis/${editingApi.id}/`, values);
        message.success('更新成功');
      } else {
        console.log('[ApiManage] 新增API:', values);
        await axios.post('/api/modelapis/', values);
        message.success('添加成功');
      }
      setModalVisible(false);
      fetchApis();
    } catch (err) {
      console.error('[ApiManage] 保存API失败:', err);
      let errorMsg = '保存失败';
      if (err.response && err.response.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else {
          errorMsg = Object.values(err.response.data).join('; ');
        }
      }
      message.error(errorMsg);
    }
  };

  // 删除API
  const handleDelete = async (id) => {
    try {
      console.log('[ApiManage] 删除API:', id);
      await axios.delete(`/api/modelapis/${id}/`);
      message.success('删除成功');
      fetchApis();
    } catch (err) {
      console.error('[ApiManage] 删除API失败:', err);
      message.error('删除失败');
    }
  };

  // 刷新token用量并刷新表格
  const handleRefreshUsage = async () => {
    setLoading(true);
    try {
      console.log('[ApiManage] 请求刷新所有大模型API的token用量...');
      await axios.post('/api/modelapis/refresh_usage/');
      message.success('token用量已刷新');
      fetchApis();
    } catch (err) {
      console.error('[ApiManage] 刷新token用量失败:', err);
      message.error('刷新token用量失败');
    } finally {
      setLoading(false);
    }
  };

  // 过滤API列表
  const filteredApis = apis.filter(api => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      api.model_display?.toLowerCase().includes(searchLower) ||
      api.model_name?.toLowerCase().includes(searchLower) ||
      api.model_name_description?.toLowerCase().includes(searchLower) ||
      api.apikey?.toLowerCase().includes(searchLower)
    );
  });

  // 表格列定义
  const columns = [
    {
      title: '大模型',
      dataIndex: 'model_display',
      key: 'model',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.model_name && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              <Tag color="blue" size="small">{record.model_name}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'API-key',
      dataIndex: 'apikey',
      key: 'apikey',
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace' }}>
            {text.length > 20 ? `${text.substring(0, 20)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Base URL',
      dataIndex: 'base_url',
      key: 'base_url',
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ fontSize: '12px' }}>
            {text && text.length > 25 ? `${text.substring(0, 25)}...` : text || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Model Name描述',
      dataIndex: 'model_name_description',
      key: 'model_name_description',
      render: (text, record) => (
        <div>
          {text ? (
            <Tooltip title={text}>
              <span style={{ cursor: 'pointer' }}>
                {text.length > 30 ? `${text.substring(0, 30)}...` : text}
              </span>
            </Tooltip>
          ) : (
            <span style={{ color: '#999' }}>暂无描述</span>
          )}
        </div>
      ),
    },
    {
      title: 'token用量',
      dataIndex: 'usage',
      key: 'usage',
    },
    {
      title: '接入时间',
      dataIndex: 'time',
      key: 'time',
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleAddEdit(record)}>编辑</Button>
          <Popconfirm title="确定要删除这个API吗？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px #e5e5e5', padding: 32, width: '100%', minHeight: 500 }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 22, color: '#3b6ed6', fontWeight: 600 }}>API接口管理</span>
        <div>
          <Button icon={<ReloadOutlined />} onClick={handleRefreshUsage} style={{ marginRight: 12, minWidth: 80, background: theme.BUTTON_BG, color: '#231a17' }}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleAddEdit()}
            style={{ minWidth: 120, background: theme.BUTTON_BG, color: '#231a17' }}
          >
            接入大模型
          </Button>
        </div>
      </div>

      {/* 搜索框 */}
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="搜索大模型、Model Name或描述..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
          allowClear
        />
      </div>

      <Table columns={columns} dataSource={filteredApis} rowKey="id" loading={loading} />
      
      <Modal
        title={editingApi ? '编辑API' : '新增API'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        destroyOnHidden
        width={600}
      >
        <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
          {/* 大模型下拉选择 */}
          <Form.Item name="model" label="大模型" rules={[{ required: true, message: '请选择大模型' }]}> 
            <Select options={modelOptions} placeholder="请选择大模型" />
          </Form.Item>
          
          {/* API-key 输入 */}
          <Form.Item 
            name="apikey" 
            label={
              <span>
                API-key 
                <Tooltip title="同一个API Key可以配置多个不同的Model Name">
                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                </Tooltip>
              </span>
            }
            rules={[{ required: true, message: '请输入API-key' }]}
          > 
            <Input.Password allowClear />
          </Form.Item>
          
          {/* Base URL配置 */}
          <Form.Item name="base_url" label="Base URL" rules={[{ required: true, message: '请输入Base URL' }]}> 
            <Input allowClear placeholder="例如: https://api.openai.com/v1" />
          </Form.Item>
          
          {/* Model Name配置 */}
          <Form.Item name="model_name" label="Model Name" rules={[{ required: true, message: '请输入Model Name' }]}> 
            <Input allowClear placeholder="例如: gpt-4o, deepseek-chat" />
          </Form.Item>

          {/* Model Name描述 */}
          <Form.Item 
            name="model_name_description" 
            label={
              <span>
                Model Name描述 
                <Tooltip title="描述这个Model Name的用途、特点或适用场景，方便后续选择">
                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                </Tooltip>
              </span>
            }
          > 
            <Input.TextArea 
              rows={3} 
              placeholder="例如：最新的GPT-4模型，支持多模态，性能优秀，适合复杂任务" 
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* 常用Model Name推荐 */}
          <Form.Item label="常用Model Name推荐">
            <div style={{ marginTop: 8 }}>
              {form.getFieldValue('model') && commonModelNames[form.getFieldValue('model')] && (
                <div>
                  {commonModelNames[form.getFieldValue('model')].map((item, index) => (
                    <Tag
                      key={index}
                      style={{ marginBottom: 8, cursor: 'pointer' }}
                      onClick={() => {
                        form.setFieldsValue({
                          model_name: item.value,
                          model_name_description: item.description
                        });
                      }}
                    >
                      {item.label}
                    </Tag>
                  ))}
                </div>
              )}
              {(!form.getFieldValue('model') || !commonModelNames[form.getFieldValue('model')]) && (
                <span style={{ color: '#999' }}>请先选择大模型类型</span>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ApiManage;

// 导出函数，供ChatPanel使用，返回当前已配置的API列表，格式为 { model: { baseURL, apiKey, model } }
export const getModelApiConfig = () => {
  const config = {};
  const apis = window.__apis || [];
  apis.forEach(api => {
    config[api.model] = {
      baseURL: api.base_url || api.baseurl || api.url || '',
      apiKey: api.apikey,
      model: api.model_name || api.name || api.model || ''
    };
  });
  return config;
}; 